/**
 * User Detail Modal - חלון פרטי משתמש
 * Show detailed user information with risk history, questionnaire answers, and agreement
 */

import { useEffect, useRef, useState } from 'react'
import { useAdminStore } from '../../store/adminStore'
import {
  X, Mail, Phone, Calendar, Shield, TrendingUp, Clock,
  ClipboardList, FileSignature, ChevronDown, ChevronUp, Download, ShieldCheck, Cpu
} from 'lucide-react'
import api from '../../services/api'
import { roleLabel } from '../../config/roles'
import { useDialogA11y } from '../../hooks/useDialogA11y'

interface UserDetailModalProps {
  userId: string
  onClose: () => void
}

interface QuestionnaireData {
  _id: string
  status: string
  normalizedScore: number
  rawScore: number
  riskProfile?: { levelHebrew: string; description: string }
  responses: { questionId: string; question: string; answer: string; score: number }[]
  completedAt?: string
  expiresAt?: string
}

export default function UserDetailModal({ userId, onClose }: UserDetailModalProps) {
  const { selectedUser, usersLoading, fetchUserById } = useAdminStore()
  const [questionnaires, setQuestionnaires] = useState<QuestionnaireData[]>([])
  const [expandedQuestionnaire, setExpandedQuestionnaire] = useState<string | null>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  useDialogA11y(true, dialogRef, onClose)

  useEffect(() => {
    fetchUserById(userId)
    // Also fetch questionnaire data from the API response
    api.get(`/admin/users/${userId}`).then(res => {
      setQuestionnaires(res.data?.questionnaires || [])
    }).catch(() => {})
  }, [userId])

  if (usersLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <p>טוען פרטים...</p>
        </div>
      </div>
    )
  }

  if (!selectedUser) return null

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('he-IL', { year: 'numeric', month: 'long', day: 'numeric' })

  const formatDateTime = (d: string) =>
    new Date(d).toLocaleString('he-IL')

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" dir="rtl">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-detail-modal-title"
        className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 id="user-detail-modal-title" className="text-xl font-heading font-bold">
            פרטי משתמש - {selectedUser.firstName} {selectedUser.lastName}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="סגירה">
            <X size={24} aria-hidden="true" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <section>
            <h3 className="text-lg font-semibold mb-4">מידע בסיסי</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <Mail size={20} className="text-gray-400" aria-hidden="true" />
                <div>
                  <p className="text-sm text-gray-600">דוא״ל</p>
                  <p className="font-medium font-mono text-sm">{selectedUser.email}</p>
                </div>
              </div>
              {selectedUser.phoneNumber && (
                <div className="flex items-center gap-3">
                  <Phone size={20} className="text-gray-400" aria-hidden="true" />
                  <div>
                    <p className="text-sm text-gray-600">טלפון</p>
                    <p className="font-medium">{selectedUser.phoneNumber}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3">
                <Shield size={20} className="text-gray-400" aria-hidden="true" />
                <div>
                  <p className="text-sm text-gray-600">תפקיד</p>
                  <p className="font-medium">
                    {roleLabel(selectedUser.role)}
                    {selectedUser.role === 'sub_admin' && selectedUser.managedTracks?.length
                      ? ` · ${selectedUser.managedTracks.join(', ')}`
                      : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar size={20} className="text-gray-400" aria-hidden="true" />
                <div>
                  <p className="text-sm text-gray-600">תאריך רישום</p>
                  <p className="font-medium">{formatDate(selectedUser.createdAt)}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Security Info */}
          <section className="border-t pt-6">
            <h3 className="text-lg font-semibold mb-4">אבטחה</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="card bg-gray-50">
                <p className="text-sm text-gray-600 mb-1">סטטוס</p>
                <p className="font-medium">
                  {selectedUser.status === 'active' ? 'פעיל'
                    : selectedUser.status === 'suspended' ? 'מושעה'
                    : selectedUser.status === 'pending_verification' ? 'ממתין לאימות' : 'נמחק'}
                </p>
              </div>
              <div className="card bg-gray-50">
                <p className="text-sm text-gray-600 mb-1">אימות דו-שלבי</p>
                <p className="font-medium">{selectedUser.mfaEnabled ? '✓ פעיל' : '✗ לא פעיל'}</p>
              </div>
              <div className="card bg-gray-50">
                <p className="text-sm text-gray-600 mb-1">דוא״ל מאומת</p>
                <p className="font-medium">{selectedUser.emailVerified ? '✓ מאומת' : '✗ לא מאומת'}</p>
              </div>
            </div>
            {selectedUser.lastLoginAt && (
              <div className="mt-4 flex items-center gap-3">
                <Clock size={20} className="text-gray-400" aria-hidden="true" />
                <div>
                  <p className="text-sm text-gray-600">התחברות אחרונה</p>
                  <p className="font-medium">{formatDateTime(selectedUser.lastLoginAt)}</p>
                  {selectedUser.lastLoginIp && (
                    <p className="text-xs text-gray-500 font-mono">{selectedUser.lastLoginIp}</p>
                  )}
                </div>
              </div>
            )}
          </section>

          {/* Agreement */}
          <section className="border-t pt-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FileSignature size={20} aria-hidden="true" />
              הסכם ייעוץ
            </h3>
            {selectedUser.agreementSigned ? (
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="card bg-green-50 border-green-200">
                    <p className="text-sm text-gray-600 mb-1">סטטוס</p>
                    <p className="font-medium text-green-700">✓ נחתם</p>
                  </div>
                  <div className="card bg-gray-50">
                    <p className="text-sm text-gray-600 mb-1">תאריך חתימה</p>
                    <p className="font-medium">
                      {selectedUser.agreementSignedAt ? formatDateTime(selectedUser.agreementSignedAt) : '—'}
                    </p>
                  </div>
                </div>
                {selectedUser.agreementSignature && (
                  <div className="card bg-blue-50 border-blue-200">
                    <p className="text-sm text-gray-600 mb-1">חתימה דיגיטלית</p>
                    <p className="text-2xl font-bold text-blue-800" style={{ fontFamily: 'cursive' }}>
                      {selectedUser.agreementSignature}
                    </p>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex gap-4 text-xs text-gray-500">
                    {selectedUser.agreementVersion && (
                      <span>גרסת הסכם: {selectedUser.agreementVersion}</span>
                    )}
                    {selectedUser.agreementSignedIp && (
                      <span>IP: <span className="font-mono">{selectedUser.agreementSignedIp}</span></span>
                    )}
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        const response = await api.get(
                          `/admin/users/${userId}/agreement-pdf`,
                          { responseType: 'blob' }
                        )
                        const blob = new Blob([response.data], { type: 'application/pdf' })
                        const url = window.URL.createObjectURL(blob)
                        const link = document.createElement('a')
                        link.href = url
                        link.download = `agreement-${userId}.pdf`
                        document.body.appendChild(link)
                        link.click()
                        document.body.removeChild(link)
                        window.URL.revokeObjectURL(url)
                      } catch (err) {
                        alert('שגיאה בהורדת ההסכם')
                      }
                    }}
                    className="btn btn-outline text-xs py-1.5 flex items-center gap-1"
                  >
                    <Download size={14} aria-hidden="true" />
                    הורד PDF
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 bg-gray-50 rounded-xl">
                <p className="text-gray-500">ההסכם טרם נחתם</p>
              </div>
            )}
          </section>

          {/* Risk Approval */}
          <section className="border-t pt-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <ShieldCheck size={20} aria-hidden="true" />
              אישור רמת סיכון
            </h3>
            {selectedUser.riskApprovalSigned ? (
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="card bg-green-50 border-green-200">
                    <p className="text-sm text-gray-600 mb-1">סטטוס</p>
                    <p className="font-medium text-green-700">✓ נחתם</p>
                  </div>
                  <div className="card bg-gray-50">
                    <p className="text-sm text-gray-600 mb-1">רמת סיכון מאושרת</p>
                    <p className="text-2xl font-bold text-primary-600">
                      {selectedUser.approvedRiskScore}<span className="text-sm text-gray-500">/5</span>
                    </p>
                  </div>
                  <div className="card bg-gray-50">
                    <p className="text-sm text-gray-600 mb-1">תאריך חתימה</p>
                    <p className="font-medium">
                      {selectedUser.riskApprovalSignedAt ? formatDateTime(selectedUser.riskApprovalSignedAt) : '—'}
                    </p>
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={async () => {
                      try {
                        const response = await api.get(
                          `/admin/users/${userId}/risk-approval-pdf`,
                          { responseType: 'blob' }
                        )
                        const blob = new Blob([response.data], { type: 'application/pdf' })
                        const url = window.URL.createObjectURL(blob)
                        const link = document.createElement('a')
                        link.href = url
                        link.download = `risk-approval-${userId}.pdf`
                        document.body.appendChild(link)
                        link.click()
                        document.body.removeChild(link)
                        window.URL.revokeObjectURL(url)
                      } catch (err) {
                        alert('שגיאה בהורדת אישור הסיכון')
                      }
                    }}
                    className="btn btn-outline text-xs py-1.5 flex items-center gap-1"
                  >
                    <Download size={14} aria-hidden="true" />
                    הורד PDF אישור סיכון
                  </button>
                </div>
              </div>
            ) : selectedUser.lastQuestionnaireCompletedAt ? (
              <div className="text-center py-6 bg-amber-50 rounded-xl">
                <p className="text-amber-700">המשתמש השלים שאלון אך טרם חתם על אישור רמת סיכון</p>
              </div>
            ) : (
              <div className="text-center py-6 bg-gray-50 rounded-xl">
                <p className="text-gray-500">טרם הושלם שאלון — אישור רמת סיכון לא רלוונטי</p>
              </div>
            )}
          </section>

          {/* AI Model Override */}
          <ModelOverrideSection userId={userId} currentModel={selectedUser.assignedModel} />

          {/* Risk Profile */}
          <section className="border-t pt-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <TrendingUp size={20} aria-hidden="true" />
              פרופיל סיכון
            </h3>
            {selectedUser.currentRiskScore ? (
              <div>
                <div className="flex items-baseline gap-4 mb-4">
                  <div>
                    <p className="text-sm text-gray-600">ציון נוכחי</p>
                    <p className="text-3xl font-bold text-primary-600">
                      {selectedUser.currentRiskScore}
                      <span className="text-lg text-gray-500">/5</span>
                    </p>
                  </div>
                  {selectedUser.questionnaireExpiresAt && (
                    <div>
                      <p className="text-sm text-gray-600">תוקף</p>
                      <p className="font-medium">
                        עד {new Date(selectedUser.questionnaireExpiresAt).toLocaleDateString('he-IL')}
                      </p>
                      {new Date(selectedUser.questionnaireExpiresAt) < new Date() && (
                        <span className="text-xs text-red-600">פג תוקף</span>
                      )}
                    </div>
                  )}
                </div>
                {selectedUser.riskScoreHistory && selectedUser.riskScoreHistory.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">היסטוריית ציונים</p>
                    <div className="space-y-2">
                      {selectedUser.riskScoreHistory.slice(0, 5).map((history, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded">
                          <span className="font-medium">{history.score}/5</span>
                          <span className="text-gray-600">
                            {new Date(history.calculatedAt).toLocaleDateString('he-IL')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-6 bg-gray-50 rounded-xl">
                <p className="text-gray-500">המשתמש עדיין לא השלים שאלון סיכון</p>
              </div>
            )}
          </section>

          {/* Questionnaire Answers */}
          {questionnaires.length > 0 && (
            <section className="border-t pt-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <ClipboardList size={20} aria-hidden="true" />
                תשובות שאלון ({questionnaires.length})
              </h3>
              <div className="space-y-3">
                {questionnaires.map((q) => {
                  const isExpanded = expandedQuestionnaire === q._id
                  return (
                    <div key={q._id} className="border border-gray-200 rounded-xl overflow-hidden">
                      <button
                        onClick={() => setExpandedQuestionnaire(isExpanded ? null : q._id)}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 text-right"
                      >
                        <div className="flex items-center gap-3">
                          {isExpanded ? <ChevronUp size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
                          <div>
                            <span className="text-sm font-medium text-gray-900">
                              שאלון — ציון {q.normalizedScore}/5
                            </span>
                            {q.riskProfile && (
                              <span className="text-xs text-gray-500 mr-2">({q.riskProfile.levelHebrew})</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            q.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {q.status === 'completed' ? 'הושלם' : q.status}
                          </span>
                          {q.completedAt && (
                            <span className="text-xs text-gray-400">
                              {new Date(q.completedAt).toLocaleDateString('he-IL')}
                            </span>
                          )}
                        </div>
                      </button>

                      {isExpanded && q.responses && q.responses.length > 0 && (
                        <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
                          <div className="space-y-3">
                            {q.responses.map((r, i) => (
                              <div key={i} className="bg-white rounded-lg p-3 border border-gray-100">
                                <div className="flex items-start justify-between gap-2">
                                  <p className="text-sm font-medium text-gray-800">
                                    {r.questionId}. {r.question}
                                  </p>
                                  <span className="flex-shrink-0 text-xs font-medium bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">
                                    {r.score} נק׳
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600 mt-1 bg-gray-50 rounded px-2 py-1">
                                  {r.answer}
                                </p>
                              </div>
                            ))}
                          </div>
                          <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between text-xs text-gray-500">
                            <span>ציון גולמי: {q.rawScore} | ציון מנורמל: {q.normalizedScore}/5</span>
                            {q.expiresAt && <span>תוקף עד: {new Date(q.expiresAt).toLocaleDateString('he-IL')}</span>}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </section>
          )}

          {/* GDPR Consent */}
          {selectedUser.gdprConsent && (
            <section className="border-t pt-6">
              <h3 className="text-lg font-semibold mb-4">הסכמות GDPR</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span>עיבוד נתונים</span>
                  <span className="font-medium">{selectedUser.gdprConsent.dataProcessing ? '✓ הסכים' : '✗ לא הסכים'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>דיוור שיווקי</span>
                  <span className="font-medium">{selectedUser.gdprConsent.marketingEmails ? '✓ הסכים' : '✗ לא הסכים'}</span>
                </div>
                <div className="flex items-center justify-between text-gray-600">
                  <span>תאריך הסכמה</span>
                  <span>{new Date(selectedUser.gdprConsent.consentDate).toLocaleDateString('he-IL')}</span>
                </div>
              </div>
            </section>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 flex justify-end">
          <button onClick={onClose} className="btn btn-primary">סגור</button>
        </div>
      </div>
    </div>
  )
}

/** Inline component for per-user model override */
function ModelOverrideSection({ userId, currentModel }: { userId: string; currentModel?: string | null }) {
  const { models, fetchModels, setUserModel, currentDefaultModel } = useAdminStore()
  const [selected, setSelected] = useState(currentModel || '')
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState('')

  useEffect(() => {
    if (models.length === 0) fetchModels()
  }, [])

  useEffect(() => {
    setSelected(currentModel || '')
  }, [currentModel])

  const handleSave = async () => {
    setSaving(true)
    setMsg('')
    try {
      await setUserModel(userId, selected || null)
      setMsg(selected ? 'מודל ייעודי נשמר' : 'הוחזר לברירת מחדל')
      setTimeout(() => setMsg(''), 3000)
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="border-t pt-6">
      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <Cpu size={20} aria-hidden="true" />
        מודל AI
      </h3>
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            aria-label="בחירת מודל AI למשתמש"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">ברירת מחדל{currentDefaultModel ? ` (${currentDefaultModel})` : ''}</option>
            {models.map((m) => (
              <option key={m.id} value={m.id}>
                {m.displayName} — {m.id}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || selected === (currentModel || '')}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? '...' : 'שמור'}
        </button>
      </div>
      {msg && <p className="text-green-600 text-xs mt-2">{msg}</p>}
    </section>
  )
}
